import { FC, useEffect, useState, useContext, useRef } from "react"
import { Doced, EditingDocedBoxFeature } from "ramper"
import { ProportionalSizeContainer, getFileName } from "resha"
import { AnnotationFieldContent } from "universal-schema/lib/1.0.0/annotation"

import {
    AnnotationFieldsStateContext,
    BoundingBoxesType
} from "../AnnotationFieldsStateContext"
import { BoundingBoxStatus } from "../constants"
import { isKeyValueAnnotation, isTableAnnotation } from "../utils"
import { DocumentHeader } from "./DocumentHeader"

interface DocumentExtractionTaskItemProps {
    onBack?: () => void
}

export const DocumentRenderer: FC<DocumentExtractionTaskItemProps> = ({
    onBack
}) => {
    const {
        changed,
        status,
        visibleDocument,
        showFieldModal,
        currentAnnotations,
        rendererHelpers,
        currentBoundingBoxes,
        setChanged,
        setVisiblePage,
        setSelectedId,
        setCurrentAnnotations,
        setCurrentBoundingBoxes,
        setRendererHelpers,
        makeCurrentBoundingBoxesActive
    } = useContext(AnnotationFieldsStateContext)

    const mode = showFieldModal ? "edit" : "preview"
    const fileName = getFileName(visibleDocument)

    const containerRef = useRef<HTMLDivElement | null>(null)
    const modeRef = useRef<"preview" | "edit">(mode)

    const [exported, setExported] = useState<Doced.DocedExport | undefined>()
    const [docedChanged, setDocedChanged] = useState<boolean>(false)

    const checkIsActiveBoundingBox = () => {
        if (
            status === BoundingBoxStatus.NON_TRUSTED ||
            status === BoundingBoxStatus.TRUSTED
        ) {
            makeCurrentBoundingBoxesActive()
        }
    }

    const changeAnnotationContent = () => {
        if (currentAnnotations) {
            if (isKeyValueAnnotation(currentAnnotations[0])) {
                // TODO: change content of key,value annotation
            } else if (isTableAnnotation(currentAnnotations[0])) {
                // TODO: change content of table annotation
            } else {
                const editingFeature = exported?.documentInformation
                    .editingFeature as EditingDocedBoxFeature
                if (editingFeature) {
                    const index = currentBoundingBoxes?.boundingBoxes.findIndex(
                        ({ id }) => id === editingFeature.id
                    )
                    if (
                        index !== undefined &&
                        index >= 0 &&
                        editingFeature.inferredText
                    ) {
                        const newAnnotations = [...currentAnnotations]
                        ;(
                            newAnnotations[index] as AnnotationFieldContent
                        ).content = editingFeature.inferredText
                        rendererHelpers?.setShowingPage(
                            editingFeature.pageNumber
                        )
                        setVisiblePage(editingFeature.pageNumber)
                        setCurrentAnnotations(newAnnotations)
                    }
                }
            }
        }
    }

    const handleExport = (data: Doced.DocedExport, dataChanged: boolean) => {
        setExported(data)
        if (data !== undefined) {
            if (modeRef.current === "preview") {
                if (!data.documentInformation.editingFeature) {
                    setSelectedId(data.selectedInstances?.[0])
                } else {
                    // hold any post submitted changes, like async OCR
                    setCurrentBoundingBoxes(
                        (boundingBoxes?: BoundingBoxesType) =>
                            boundingBoxes
                                ? {
                                      ...boundingBoxes
                                  }
                                : undefined
                    )
                }
            } else {
                setDocedChanged(dataChanged)
            }
        }
    }

    useEffect(() => {
        if (docedChanged) {
            checkIsActiveBoundingBox()
            changeAnnotationContent()
            if (!changed) {
                setChanged(true)
            }
            setDocedChanged(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docedChanged])

    useEffect(() => {
        if (containerRef.current !== null && rendererHelpers !== undefined) {
            const container = containerRef.current

            rendererHelpers?.mountTo(containerRef.current)

            return () => rendererHelpers.unmountFrom(container)
        }
    }, [rendererHelpers])

    useEffect(() => {
        if (rendererHelpers === undefined || visibleDocument === undefined) {
            return
        }

        rendererHelpers.setDocument(visibleDocument)
    }, [rendererHelpers, visibleDocument])

    useEffect(() => {
        modeRef.current = mode

        if (rendererHelpers === undefined) {
            return
        }

        rendererHelpers.setConfig({
            disableInteraction: mode !== "edit"
        })
    }, [mode, rendererHelpers])

    useEffect(() => {
        if (rendererHelpers === undefined || visibleDocument === undefined) {
            return
        }

        rendererHelpers.initValue(
            currentBoundingBoxes?.boundingBoxes || [],
            visibleDocument
        )
    }, [currentBoundingBoxes, visibleDocument, rendererHelpers])

    useEffect(() => {
        if (exported?.documentInformation.visiblePage !== undefined) {
            setVisiblePage(exported?.documentInformation.visiblePage)
            if (
                modeRef.current === "edit" &&
                status === BoundingBoxStatus.ACTIVE &&
                currentBoundingBoxes
            ) {
                setCurrentBoundingBoxes({
                    shapesState: currentBoundingBoxes.shapesState,
                    boundingBoxes: currentBoundingBoxes.boundingBoxes.map(
                        boundingBox => ({
                            ...boundingBox,
                            pageNumber:
                                exported?.documentInformation.visiblePage
                        })
                    )
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        exported?.documentInformation.visiblePage,
        setVisiblePage,
        setCurrentBoundingBoxes
    ])

    useEffect(() => {
        const render = async () => {
            const config = {
                disableInteraction: modeRef.current === "preview",
                enableExportOnChange: true,
                enableSubAnnotation: true,
                enableAutoDetection: true,
                deferClassSelection: false,
                showNavigation: false,
                choices: [],
                onExport: handleExport
            }
            const result = await Doced.render(config)
            setRendererHelpers(result.helpers)
        }
        render()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setRendererHelpers])

    return (
        <div className="w-full h-full flex-grow flex-shrink flex items-stretch relative">
            <DocumentHeader
                fileName={fileName}
                zoom={exported?.documentInformation.zoom}
                defaultZoom={exported?.documentInformation.defaultZoom}
                currentPage={exported?.documentInformation?.visiblePage}
                totalPages={exported?.documentInformation?.totalPages}
                helpers={rendererHelpers}
                onBack={onBack}
            />
            <ProportionalSizeContainer
                testId="document-extraction-tool"
                className="w-0 flex-grow flex-shrink"
                ref={containerRef}
            />
        </div>
    )
}
